import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertCircle, Loader2, UserPlus, Globe } from 'lucide-react';
import axios from 'axios';
import { API, BACKEND_URL } from '../utils/api';
import { PAYMIG_LOGO_B64 } from '../utils/paymigLogo';

export default function GuestPaymentReturn() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const urlPaymentId = searchParams.get('payment_id');
  const result = searchParams.get('result');
  const nonce = searchParams.get('n');

  const storedPaymentId = sessionStorage.getItem('guest_payment_id');
  const storedNonce = sessionStorage.getItem('guest_payment_nonce');
  const storedAmount = sessionStorage.getItem('guest_payment_amount');
  const storedRecipient = sessionStorage.getItem('guest_payment_recipient');

  const paymentId = urlPaymentId || storedPaymentId;
  const paymentNonce = nonce || storedNonce;
  const wsRef = useRef(null);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'sv' ? 'en' : 'sv');
  };

  // WebSocket for real-time payment status updates
  useEffect(() => {
    if (!paymentId || status === 'success' || status === 'cancelled') return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'subscribe_payment', payment_id: paymentId }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'payment_status' && data.payment_id === paymentId) {
            if (data.status === 'COMPLETED') {
              setStatus('success');
              setPaymentInfo({
                amount: data.amount,
                recipient: data.recipient_name,
                subAccount: data.sub_account_name,
              });
              toast.success(t('guestPayment.paymentSuccessful'));
            } else if (data.status === 'CANCELLED') {
              setStatus('cancelled');
            }
          }
        } catch (e) { /* ignore parse errors */ }
      };

      ws.onerror = () => { /* silent — fallback to polling */ };
    } catch (e) { /* WS not available — fallback to polling */ }

    return () => {
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, [paymentId, status]);

  useEffect(() => {
    const processReturn = async () => {
      sessionStorage.removeItem('guest_payment_id');
      sessionStorage.removeItem('guest_payment_nonce');
      sessionStorage.removeItem('guest_payment_amount');
      sessionStorage.removeItem('guest_payment_recipient');

      if (!paymentId) {
        setStatus('error');
        return;
      }

      if (result && paymentId) {
        try {
          let decodedResult = result;
          try {
            if (result.includes('%')) decodedResult = decodeURIComponent(result);
          } catch (e) { /* ignore */ }

          const res = await axios.post(`${API}/guest/payment/${paymentId}/callback`, {
            result: decodedResult,
            nonce: paymentNonce,
          });

          if (res.data.payment_status === 'COMPLETED') {
            setStatus('success');
            setPaymentInfo({
              amount: res.data.amount,
              recipient: res.data.recipient_name,
              subAccount: res.data.sub_account_name,
            });
            toast.success(t('guestPayment.paymentSuccessful'));
          } else if (res.data.payment_status === 'CANCELLED') {
            setStatus('cancelled');
          } else {
            setStatus('error');
          }
        } catch (e) {
          console.error('Guest callback error:', e);
          await checkPaymentStatus();
        }
      } else {
        await checkPaymentStatus();
      }
    };

    const checkPaymentStatus = async () => {
      if (!paymentId || !paymentNonce) {
        setStatus('pending');
        setPaymentInfo({
          amount: storedAmount,
          recipient: storedRecipient,
        });
        return;
      }
      try {
        const res = await axios.get(`${API}/guest/payment/${paymentId}/status/${paymentNonce}`);
        if (res.data.status === 'COMPLETED') {
          setStatus('success');
          setPaymentInfo({
            amount: res.data.amount,
            recipient: res.data.recipient_name || res.data.sub_account_name,
            subAccount: res.data.sub_account_name,
          });
        } else if (res.data.status === 'CANCELLED') {
          setStatus('cancelled');
        } else {
          setStatus('pending');
          setPaymentInfo({
            amount: res.data.amount || storedAmount,
            recipient: res.data.recipient_name || res.data.sub_account_name || storedRecipient,
          });
        }
      } catch (e) {
        setStatus('pending');
        setPaymentInfo({
          amount: storedAmount,
          recipient: storedRecipient,
        });
      }
    };

    processReturn();
  }, []);

  // Polling fallback: check status every 5s if still pending/processing
  useEffect(() => {
    if (status !== 'pending' && status !== 'processing') return;
    if (!paymentId || !paymentNonce) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/guest/payment/${paymentId}/status/${paymentNonce}`);
        if (res.data.status === 'COMPLETED') {
          setStatus('success');
          setPaymentInfo({
            amount: res.data.amount,
            recipient: res.data.recipient_name || res.data.sub_account_name,
            subAccount: res.data.sub_account_name,
          });
          toast.success(t('guestPayment.paymentSuccessful'));
        } else if (res.data.status === 'CANCELLED') {
          setStatus('cancelled');
        }
      } catch (e) { /* ignore */ }
    }, 5000);

    return () => clearInterval(interval);
  }, [status, paymentId, paymentNonce]);

  const statusConfig = {
    processing: {
      icon: <Loader2 className="h-14 w-14 text-teal-600 animate-spin" />,
      titleKey: 'guestPayment.processingPayment',
      descKey: 'guestPayment.pleaseWait',
    },
    success: {
      icon: <CheckCircle className="h-14 w-14 text-emerald-500" />,
      titleKey: 'guestPayment.paymentSuccessful',
      descKey: 'guestPayment.paymentReceived',
    },
    cancelled: {
      icon: <XCircle className="h-14 w-14 text-red-500" />,
      titleKey: 'guestPayment.paymentCancelled',
      descKey: 'guestPayment.cancelledDesc',
    },
    pending: {
      icon: <AlertCircle className="h-14 w-14 text-amber-500" />,
      titleKey: 'guestPayment.paymentInitiated',
      descKey: 'guestPayment.initiatedDesc',
    },
    error: {
      icon: <XCircle className="h-14 w-14 text-red-500" />,
      titleKey: 'guestPayment.somethingWentWrong',
      descKey: 'guestPayment.errorDesc',
    },
  };

  const cfg = statusConfig[status] || statusConfig.error;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-testid="guest-payment-return-page">
      {/* Logo + lang toggle */}
      <div className="flex items-center gap-3 mb-8">
        <img src={PAYMIG_LOGO_B64} alt="PayMig" className="w-10 h-10 rounded-lg" />
        <span className="text-xl font-bold text-gray-900 tracking-tight">PayMig</span>
        <button
          onClick={toggleLang}
          className="ml-3 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-200 hover:bg-gray-300 text-xs text-gray-700 transition-colors"
          data-testid="lang-toggle"
        >
          <Globe className="h-3 w-3" />
          {i18n.language === 'sv' ? 'EN' : 'SV'}
        </button>
      </div>

      <Card className="w-full max-w-md border-gray-200 shadow-lg" data-testid="guest-return-card">
        <CardContent className="pt-8 pb-6 text-center space-y-5">
          <div className="flex justify-center">{cfg.icon}</div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900" data-testid="return-status-title">{t(cfg.titleKey)}</h2>
            <p className="text-sm text-gray-500">{t(cfg.descKey)}</p>
          </div>

          {paymentInfo && (paymentInfo.amount || paymentInfo.recipient) && (
            <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 border border-gray-200" data-testid="return-payment-info">
              {paymentInfo.amount && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('guestPayment.amount')}</span>
                  <span className="text-sm text-gray-900 font-medium">{paymentInfo.amount} SEK</span>
                </div>
              )}
              {paymentInfo.recipient && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('guestPayment.to')}</span>
                  <span className="text-sm text-gray-900 font-medium">{paymentInfo.recipient}</span>
                </div>
              )}
              {paymentInfo.subAccount && paymentInfo.subAccount !== paymentInfo.recipient && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('guestPayment.for')}</span>
                  <span className="text-sm text-gray-900 font-medium">{paymentInfo.subAccount}</span>
                </div>
              )}
            </div>
          )}

          {(status === 'success' || status === 'pending') && (
            <div className="pt-2 space-y-3" data-testid="signup-cta">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <p className="text-sm text-teal-700 font-medium mb-1">{t('guestPayment.trackPayments')}</p>
                <p className="text-xs text-gray-500">{t('guestPayment.createAccountDesc')}</p>
              </div>
              <Button
                onClick={() => nav('/')}
                className="w-full h-11 bg-teal-600 hover:bg-teal-500 text-white font-medium"
                data-testid="signup-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('guestPayment.createAccount')}
              </Button>
            </div>
          )}

          {status !== 'processing' && (
            <button
              onClick={() => window.close()}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              data-testid="close-btn"
            >
              {t('guestPayment.closePage')}
            </button>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 text-center space-y-1">
        <p className="text-xs text-gray-400">{t('guestPayment.poweredBy')}</p>
        <p className="text-xs text-gray-500">support@paymig.app</p>
      </div>
    </div>
  );
}
